import React, { useState, useEffect, Fragment } from 'react';

import _ from 'lodash';
import PropTypes from 'prop-types';

import { FieldCheckbox } from './fields';
import { Error } from '../design/elements';
import FormField from './formField';
import { GroupWrapper, GroupTitle } from './css';
import {
  fieldMultisiteOrder,
  fieldStreetNameShippingAddress
} from '../utils/constant';
import { getFieldValue } from './utils/formCommon';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';

const UserDetailsForm = ({
  data,
  formErrors,
  formFields,
  orderData,
  setFormFields,
  isFormTouched,
  setIsFormTouched,
  resetSelectedFields
}) => {
  const {
    name,
    formStepFields,
    sameAsCheckBox,
    dependsOnBackendField,
    dependsOnFieldName,
    backendKey,
    isDefaultPopulated: isDefaultPopulatedField
  } = data;

  const localizedStrings = useLocalizedStringsContext();
  const {
    contentfulValues: { yes, valueShippingAddress }
  } = localizedStrings;
  const [isSameAs, setIsSameAs] = useState(false);
  const [isDefaultPopulated, setIsDefaultPopulated] = useState(
    isDefaultPopulatedField
  );
  const [isFieldReset, setIsFieldReset] = useState(false);

  const handleFormField = (fieldName, value, type) => {
    const fields = { ...formFields };
    setIsFieldReset(false);
    if (type === 'AddressDetails' && typeof value === 'object') {
      Object.keys(value).forEach(key => {
        fields[`${key}_${backendKey}`] = {
          ...fields[fieldName],
          value: value[key]
        };
      });
    } else {
      fields[fieldName] = { ...fields[fieldName], value };
    }
    if (!isFormTouched) {
      setIsFormTouched(true);
    }
    setFormFields(fields);
    // Reset Selected Fields Logic
    if (_.has(fields, `${fieldName}.resetFields`)) {
      setIsFieldReset(true);
      resetSelectedFields(_.get(fields, `${fieldName}.resetFields`, []));
    }
  };

  const checkBoxName =
    sameAsCheckBox && sameAsCheckBox.fields && sameAsCheckBox.fields.name;

  const populateFormFields = () => {
    const fields = { ...formFields };
    formStepFields.forEach(({ fields: { name: fieldName, value } }) => {
      let fieldNewValue = value;
      const fieldPrefix = fieldName.split('_')[0];

      if (isSameAs || isDefaultPopulated) {
        if (dependsOnBackendField) {
          fieldNewValue =
            getFieldValue(
              `${fieldPrefix}_${dependsOnBackendField}`,
              formFields
            ) ||
            orderData[`${fieldPrefix}_${dependsOnBackendField}`] ||
            '';
        } else if (dependsOnFieldName) {
          const itemName = `${fieldPrefix}_${dependsOnFieldName}`;
          fieldNewValue = formFields[itemName] && formFields[itemName].value;
        }
      } else {
        fieldNewValue = '';
      }
      fields[fieldName] = { ...fields[fieldName], value: fieldNewValue };
    });

    if (checkBoxName) {
      fields[checkBoxName] = {
        isVisible: true,
        checked: isSameAs,
        value: isSameAs
      };
    }

    setFormFields(fields);
  };

  useEffect(() => {
    populateFormFields();
  }, [isSameAs]);

  useEffect(() => {
    if (isDefaultPopulated && orderData && orderData.id && !isFieldReset) {
      populateFormFields();
      setIsDefaultPopulated(false);
      setIsSameAs(true);
    }
  }, [isDefaultPopulated, orderData]);

  const onIsSameAsChange = ({ target: { checked } }) => {
    setIsSameAs(checked);
    if (!isFormTouched) setIsFormTouched(true);
  };

  useEffect(() => {
    if (orderData && orderData.id && !isFieldReset) {
      const fields = { ...formFields };
      formStepFields.forEach(({ fields: { name: fieldName } }) => {
        fields[fieldName] = {
          ...fields[fieldName],
          value: orderData[fieldName]
        };
      });
      setFormFields(fields);
      if (isFormTouched) setIsFormTouched(false);
      if (orderData[checkBoxName]) setIsSameAs(true);

      /**
       * For Multi-site if detault shipping address
       * to service address if shipping address was not
       * entered already
       */
      if (
        _.get(orderData, fieldMultisiteOrder, '') === yes &&
        !_.get(orderData, fieldStreetNameShippingAddress, '') &&
        name === valueShippingAddress
      ) {
        setIsSameAs(true);
      }
    }
  }, [orderData]);

  return (
    <GroupWrapper>
      <GroupTitle>{name}</GroupTitle>
      {sameAsCheckBox && (
        <FieldCheckbox
          data={{
            ...sameAsCheckBox.fields,
            onChange: onIsSameAsChange,
            checked: isSameAs
          }}
        />
      )}
      {formStepFields.map(({ fields, sys: { id: sysId } }, index) => {
        const key = `UserDetails${name}${index}`;
        const error = formErrors[fields.name];
        const fieldData = {
          ...fields,
          value: formFields[fields.name] && formFields[fields.name].value,
          disabled: isSameAs,
          isSameAs,
          sysId,
          onChange: ({ target: { value } }) =>
            handleFormField(fields.name, value, fields.type)
        };
        return (
          <Fragment key={key}>
            <FormField data={fieldData} />
            {error && error.text && <Error text={error.text} />}
          </Fragment>
        );
      })}
    </GroupWrapper>
  );
};

UserDetailsForm.propTypes = {
  data: PropTypes.shape({
    dependsOnFieldName: PropTypes.string,
    backendKey: PropTypes.string,
    dependsOnBackendField: PropTypes.string,
    formStepFields: PropTypes.arrayOf(PropTypes.object),
    name: PropTypes.string.isRequired,
    sameAsCheckBox: PropTypes.objectOf(PropTypes.object).isRequired,
    isDefaultPopulated: PropTypes.bool
  }).isRequired,
  formErrors: PropTypes.objectOf(PropTypes.object),
  formFields: PropTypes.objectOf(PropTypes.object).isRequired,
  orderData: PropTypes.objectOf(PropTypes.object).isRequired,
  setFormFields: PropTypes.func.isRequired,
  isFormTouched: PropTypes.bool.isRequired,
  setIsFormTouched: PropTypes.func.isRequired,
  resetSelectedFields: PropTypes.func.isRequired
};

UserDetailsForm.defaultProps = {
  formErrors: {}
};

export default UserDetailsForm;
